import { Check, Undo2 } from 'lucide-react';
import { GameState } from '../types/darts';
import { useAppTheme } from '../hooks/useAppTheme';
import { useT } from '../hooks/useT';

interface ScoreboardCricketProps {
  gameState: GameState;
  onUndo: () => void;
  canUndo: boolean;
}

const CRICKET_TARGETS = [20, 19, 18, 17, 16, 15, 25];

export function ScoreboardCricket({ gameState, onUndo, canUndo }: ScoreboardCricketProps) {
  const { t, tf } = useT();
  const { theme } = useAppTheme();

  const mark = (count: number) => {
    if (count >= 3) {
      return (
        <span className={`grid h-6 w-6 place-items-center rounded-full ${theme.keypadPrimaryBg} ${theme.keypadPrimaryText}`}>
          <Check size={14} strokeWidth={3} aria-hidden />
        </span>
      );
    }
    const symbol = count === 2 ? 'X' : count === 1 ? '/' : '–';
    return <span className={`text-base font-extrabold ${count > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-400 dark:text-slate-600'}`}>{symbol}</span>;
  };

  return (
    <div className="mx-auto w-full max-w-xl space-y-2">
      <div className="flex items-center justify-between gap-2 rounded-2xl bg-black/5 px-3 py-1 dark:bg-white/5">
        <div className="flex min-h-11 items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase ${theme.badgeBg} ${theme.badgeText}`}>{t('modeCricket')}</span>
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{t('cricketSubtitle')}</span>
        </div>
        {canUndo && (
          <button
            onClick={onUndo}
            aria-label={t('undoLabel')}
            className="flex min-h-11 items-center gap-1.5 rounded-2xl bg-red-500/15 px-3 text-sm font-semibold whitespace-nowrap text-red-800 transition-all duration-150 active:scale-95 dark:text-red-300"
          >
            <Undo2 size={16} aria-hidden />
            <span className="hidden sm:inline">{t('undoShort')}</span>
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-3xl bg-white p-3 shadow-sm dark:bg-white/5 sm:p-4">
        <table className="w-full border-collapse text-center">
          <thead>
            <tr className="border-b border-black/5 dark:border-white/10">
              <th scope="col" className="px-2 py-1 text-left text-xs font-bold text-slate-600 uppercase dark:text-slate-400">
                {t('cricketTargetCol')}
              </th>
              {gameState.players.map((p, idx) => {
                const isActive = idx === gameState.activePlayerIndex;
                return (
                  <th key={p.id} scope="col" aria-current={isActive ? 'true' : undefined} className={`px-1.5 py-1 text-sm font-bold ${isActive ? theme.textAccent : ''}`}>
                    <div className="mx-auto max-w-[96px] truncate">{p.name}</div>
                    <div className={`font-mono text-xs font-bold ${theme.textAccent}`}>
                      {tf('cricketPoints', { n: gameState.cricketState[p.id]?.points || 0 })}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {CRICKET_TARGETS.map(target => {
              const label = target === 25 ? 'Bull' : String(target);
              return (
                <tr key={target} className="border-b border-black/5 last:border-0 dark:border-white/5">
                  <th scope="row" className="px-2 py-1.5 text-left">
                    <span className={`rounded-lg bg-black/5 px-2 py-0.5 font-mono text-sm font-bold dark:bg-white/10 ${theme.textAccent}`}>{label}</span>
                  </th>
                  {gameState.players.map(p => {
                    const count = gameState.cricketState[p.id]?.hits[target] || 0;
                    return (
                      <td key={p.id} className="px-1.5 py-1.5" aria-label={tf('cricketMarks', { name: p.name, n: Math.min(3, count), target: label })}>
                        <div className="flex items-center justify-center font-mono" aria-hidden>
                          {mark(count)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
